import {filter, forEach, includes, isEmpty} from 'lodash';
import {ApplicationLocale, isApplicationLocale, LocaleMessagesType} from 'src/js/common/types';
import {DEFAULT_LOCALE} from 'src/js/containers/i18n/i18n_constants';

import {FetcherUtils} from '../common/utils/fetcher_utils';

const CONFIGURATION_CONFIG = 'config';
const CONFIGURATION_LANGUAGES = 'languages';
const CONFIGURATION_USED_LANGUAGES = 'usedLanguages';
const CONFIGURATION_PREFERRED_LANGUAGE = 'preferredLanguage';

const LOCALE_LOCALE = 'locale';
const LOCALE_LABEL = 'label';

type LanguageString = string;

export class LocaleMessages {
  private readonly rawData: LocaleMessagesType;

  private readonly locale: LanguageString;

  private readonly loaded: boolean;

  constructor(rawData: LocaleMessagesType, locale = '', loaded = false) {
    this.rawData = {...rawData};
    this.locale = locale;
    this.loaded = loaded;
  }

  getLocale() {
    return this.locale;
  }

  getMessages() {
    return this.rawData;
  }

  isLoaded() {
    return this.loaded;
  }
}

interface LanguageConfigRaw {
  locale: LanguageString;
  label: string;
}

export class LanguageConfig {
  private readonly rawData: Partial<LanguageConfigRaw>;

  constructor(rawData: Partial<LanguageConfigRaw> = {}) {
    this.rawData = {...rawData};
  }

  getLocale() {
    return this.rawData[LOCALE_LOCALE];
  }

  getLabel() {
    return this.rawData[LOCALE_LABEL];
  }
}

type Languages = LanguageConfigRaw[];

interface I18nConfigConfigRaw {
  languages: Languages;
  preferredLanguage: LanguageString;
  usedLanguages: LanguageString[];
}

export interface I18nConfigRaw {
  config: I18nConfigConfigRaw;
}

export class I18nConfig {
  private readonly rawData: Partial<I18nConfigRaw>;

  private readonly loaded: boolean;

  private readonly languages: LanguageConfig[] | undefined;

  private readonly usedApplicationLanguages: LanguageConfig[] | undefined;

  static initLanguages(rawDocumentLocaleMessages: Languages): LanguageConfig[] {
    const languages: LanguageConfig[] | undefined = [];
    if (rawDocumentLocaleMessages) {
      forEach(rawDocumentLocaleMessages, (singleLocaleMessages) => {
        languages.push(new LanguageConfig(singleLocaleMessages));
      });
    }
    return languages;
  }

  static initUsedApplicationLanguages(localeMessages: LanguageConfig[] | undefined, usedLanguages: LanguageString[]): LanguageConfig[] {
    return localeMessages ? filter(localeMessages, (language) => includes(usedLanguages, language.getLocale())) : [];
  }

  constructor(rawData: Partial<I18nConfigRaw> = {}) {
    this.rawData = {...rawData};
    this.loaded = false;
    this.languages = undefined;
    this.usedApplicationLanguages = undefined;

    if (!isEmpty(this.rawData)) {
      const rawConfiguration = this.rawData[CONFIGURATION_CONFIG];
      if (rawConfiguration && rawConfiguration[CONFIGURATION_LANGUAGES]) {
        this.languages = I18nConfig.initLanguages(rawConfiguration[CONFIGURATION_LANGUAGES]);
        this.usedApplicationLanguages = I18nConfig.initUsedApplicationLanguages(this.getLanguages(), this.getUsedLanguages());
      }
      this.loaded = true;
    }
  }

  getLanguages(): LanguageConfig[] | undefined {
    return this.languages;
  }

  getUsedApplicationLanguages(): LanguageConfig[] | undefined {
    return this.usedApplicationLanguages;
  }

  getPreferredLanguage(): LanguageString | null {
    return this.rawData[CONFIGURATION_CONFIG] && this.rawData[CONFIGURATION_CONFIG][CONFIGURATION_PREFERRED_LANGUAGE]
      ? this.rawData[CONFIGURATION_CONFIG][CONFIGURATION_PREFERRED_LANGUAGE]
      : null;
  }

  getUsedLanguages(): LanguageString[] {
    return this.rawData[CONFIGURATION_CONFIG] && this.rawData[CONFIGURATION_CONFIG][CONFIGURATION_USED_LANGUAGES]
      ? this.rawData[CONFIGURATION_CONFIG][CONFIGURATION_USED_LANGUAGES]
      : [];
  }

  isLoaded() {
    return this.loaded;
  }

  /* eslint-disable import/no-dynamic-require, global-require */
  loadInternationalizationAPILocaleData() {
    if (!Intl.PluralRules) {
      require('@formatjs/intl-pluralrules/polyfill');
    }

    if (!Intl.RelativeTimeFormat) {
      require('@formatjs/intl-relativetimeformat/polyfill');
    }
    this.getUsedApplicationLanguages()?.forEach((language) => {
      // load locale data for all supported languages
      if (!Intl.PluralRules) {
        require(`@formatjs/intl-pluralrules/locale-data/${language.getLocale()}`);
      }

      if (!Intl.RelativeTimeFormat) {
        require(`@formatjs/intl-relativetimeformat/locale-data/${language.getLocale()}`);
      }
    });
  }
  /* eslint-enable import/no-dynamic-require, global-require */

  determineLocale(): ApplicationLocale {
    // set current locale and load messages
    const cookiesLocale = FetcherUtils.getCookieValue('locale');
    const preferredLocale = this.getPreferredLanguage();
    let newLocale = DEFAULT_LOCALE;
    if (cookiesLocale && isApplicationLocale(cookiesLocale) && this.getUsedLanguages().indexOf(cookiesLocale) !== -1) {
      newLocale = cookiesLocale;
    } else if (preferredLocale && isApplicationLocale(preferredLocale) && this.getUsedLanguages().indexOf(preferredLocale) !== -1) {
      newLocale = preferredLocale;
    }
    return newLocale;
  }
}

export default {I18nConfig, LanguageConfig, LocaleMessages};
